import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable, type Column } from '../../components/DataTable';
import { ErrorState, LoadingRows } from '../../components/states';
import { StatusChip } from '../../components/StatusChip';
import { Field, Panel } from '../../components/ui';
import { date, dateTime, money } from '../../lib/format';
import { errorMessage, useNotify } from '../../lib/notify';
import { Callout, FieldGrid } from '../tenants/parts';
import type { TenantDetail } from '../tenants/api';
import {
  fetchTenantWhatsapp,
  PERIOD_LABELS,
  whatsappKeys,
  type PlatformWhatsapp,
  type WhatsappPeriod,
} from './api';

const STATE_LABEL: Record<PlatformWhatsapp['plugin']['state'], string> = {
  NOT_ENABLED: 'Not enabled',
  ACTIVE: 'Active',
  GRACE: 'In grace',
  EXPIRED: 'Ended',
};
const STATE_TONE: Record<PlatformWhatsapp['plugin']['state'], string> = {
  NOT_ENABLED: 'DRAFT',
  ACTIVE: 'ACTIVE',
  GRACE: 'GRACE',
  EXPIRED: 'EXPIRED',
};

const intIn = (v: string, min: number, max: number) => {
  if (!/^\d+$/.test(v.trim())) return null;
  const n = Number(v);
  return n >= min && n <= max ? n : null;
};

/**
 * Tenant → WhatsApp: the paid add-on. Enable / extend with the period, its own grace days and the
 * amount charged (it varies per client), change the grace, or turn it off. Every change is kept in
 * the history and counted in WhatsApp revenue.
 */
export function WhatsappTenantTab({ tenant }: { tenant: Pick<TenantDetail, 'id' | 'name'> }) {
  const { can } = useAuth();
  const manage = can('subscriptions.manage');
  const q = useQuery({
    queryKey: whatsappKeys.tenant(tenant.id),
    queryFn: () => fetchTenantWhatsapp(tenant.id),
  });
  const [dialog, setDialog] = useState<'extend' | 'grace' | 'disable' | null>(null);

  if (q.isPending) return <LoadingRows rows={4} />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const { plugin, periods } = q.data;
  const enabled = plugin.state !== 'NOT_ENABLED';

  const columns: Column<WhatsappPeriod>[] = [
    { key: 'when', header: 'When', render: (p) => dateTime(p.createdAt) },
    { key: 'kind', header: 'Change', render: (p) => PERIOD_LABELS[p.kind] },
    {
      key: 'period',
      header: 'Period',
      render: (p) => (p.months ? `${String(p.months)} mo` : p.days ? `${String(p.days)} d` : '—'),
    },
    {
      key: 'through',
      header: 'Paid through',
      render: (p) => date(p.paidThroughAfter),
      hideBelow: 'sm',
    },
    { key: 'grace', header: 'Grace', render: (p) => `${String(p.graceDays)} d`, hideBelow: 'md' },
    {
      key: 'amount',
      header: 'Charged',
      align: 'right',
      render: (p) => (p.amount === null ? '—' : money(p.amount)),
    },
    { key: 'by', header: 'By', render: (p) => p.admin.name, hideBelow: 'md' },
    { key: 'notes', header: 'Notes', render: (p) => p.notes ?? '', hideBelow: 'lg' },
  ];

  return (
    <Stack spacing={2.5}>
      <Panel
        title={
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <WhatsAppIcon sx={{ color: '#1DA851' }} />
            <span>WhatsApp add-on</span>
            <StatusChip status={STATE_TONE[plugin.state]} label={STATE_LABEL[plugin.state]} />
          </Stack>
        }
        subtitle="Paid separately from the subscription. Hidden from the shop until enabled."
        action={
          manage && (
            <Stack direction="row" spacing={1}>
              {enabled && (
                <>
                  <Button variant="outlined" color="error" onClick={() => setDialog('disable')}>
                    Turn off
                  </Button>
                  <Button variant="outlined" onClick={() => setDialog('grace')}>
                    Grace
                  </Button>
                </>
              )}
              <Button variant="contained" onClick={() => setDialog('extend')}>
                {enabled ? 'Extend' : 'Enable'}
              </Button>
            </Stack>
          )
        }
      >
        {enabled ? (
          <FieldGrid>
            <Field label="Paid through">{date(plugin.paidThrough)}</Field>
            <Field label="Grace">
              {plugin.graceDays ?? 0} days · until {date(plugin.graceEndsAt)}
            </Field>
            <Field label="Shop switch">{plugin.shopEnabled ? 'Shown' : 'Hidden by the shop'}</Field>
            <Field label="Number">{plugin.linked ? 'Linked' : 'Not linked yet'}</Field>
            <Field label="Charged in total">{money(plugin.totalCharged)}</Field>
          </FieldGrid>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Not enabled for {tenant.name}.{' '}
            {Number(plugin.totalCharged) > 0 &&
              `Charged ${money(plugin.totalCharged)} before it was turned off.`}
          </Typography>
        )}
      </Panel>
      <Panel title="WhatsApp history" padded={false}>
        <DataTable
          columns={columns}
          rows={periods}
          rowKey={(p) => p.id}
          empty={<Box sx={{ p: 3, color: 'text.secondary' }}>No changes yet.</Box>}
        />
      </Panel>
      {dialog === 'extend' && (
        <ExtendWhatsappDialog tenant={tenant} q={q.data} onClose={() => setDialog(null)} />
      )}
      {dialog === 'grace' && (
        <GraceDialog
          tenant={tenant}
          current={plugin.graceDays ?? 7}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'disable' && <DisableDialog tenant={tenant} onClose={() => setDialog(null)} />}
    </Stack>
  );
}

function useDone(tenantId: string, onClose: () => void) {
  const qc = useQueryClient();
  const notify = useNotify();
  return (msg: string) => {
    notify.success(msg);
    void qc.invalidateQueries({ queryKey: whatsappKeys.tenant(tenantId) });
    void qc.invalidateQueries({ queryKey: whatsappKeys.revenue });
    onClose();
  };
}

function ExtendWhatsappDialog({
  tenant,
  q,
  onClose,
}: {
  tenant: Pick<TenantDetail, 'id' | 'name'>;
  q: PlatformWhatsapp;
  onClose: () => void;
}) {
  const done = useDone(tenant.id, onClose);
  const fee = useQuery({
    queryKey: ['platform', 'settings'],
    queryFn: () => unwrap(api.GET('/platform/settings')),
  });
  const monthly = Number(fee.data?.whatsappMonthlyFee ?? 3000);
  const enabled = q.plugin.state !== 'NOT_ENABLED';
  const [key] = useState(() => crypto.randomUUID());
  const [unit, setUnit] = useState<'months' | 'days'>('months');
  const [length, setLength] = useState('1');
  const [grace, setGrace] = useState(String(q.plugin.graceDays ?? 7));
  const [amount, setAmount] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const n = intIn(length, 1, unit === 'months' ? 60 : 1830);
  const g = intIn(grace, 0, 60);
  const suggested =
    n === null ? '' : String(unit === 'months' ? n * monthly : Math.round((n * monthly) / 30));
  const amountValue = amount ?? suggested;
  const amountOk = /^\d+(\.\d{1,2})?$/.test(amountValue.trim());

  const submit = () => {
    if (n === null || g === null || !amountOk) return;
    setBusy(true);
    setError(null);
    unwrap(
      api.POST('/platform/tenants/{id}/whatsapp/extend', {
        params: { path: { id: tenant.id }, header: { 'idempotency-key': key } },
        body: {
          ...(unit === 'months' ? { months: n } : { days: n }),
          graceDays: g,
          amount: amountValue.trim(),
          currency: 'PKR',
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
      }),
    )
      .then((r) => done(`WhatsApp paid through ${date(r.plugin.paidThrough)}`))
      .catch((e: unknown) => {
        setBusy(false);
        setError(e);
      });
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {enabled ? 'Extend WhatsApp' : 'Enable WhatsApp'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {enabled
              ? `${tenant.name} is paid through ${date(q.plugin.paidThrough)}. The period is added from that date, or from today if it has passed.`
              : `Starts today for ${tenant.name}. The shop sees WhatsApp straight away.`}
          </Typography>
          {error !== null && <Callout severity="error">{errorMessage(error)}</Callout>}
          <Stack direction="row" spacing={1.5}>
            <TextField
              label="Period"
              value={length}
              onChange={(e) => setLength(e.target.value)}
              error={n === null}
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              sx={{ flex: 1 }}
            />
            <TextField
              select
              label="Unit"
              value={unit}
              onChange={(e) => setUnit(e.target.value as 'months' | 'days')}
              sx={{ width: 130 }}
            >
              <MenuItem value="months">Months</MenuItem>
              <MenuItem value="days">Days</MenuItem>
            </TextField>
          </Stack>
          <TextField
            label="Grace days"
            value={grace}
            onChange={(e) => setGrace(e.target.value)}
            error={g === null}
            helperText="0–60. Counted from the end of the paid period."
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
          />
          <TextField
            label="Amount charged (PKR)"
            value={amountValue}
            onChange={(e) => setAmount(e.target.value)}
            error={!amountOk}
            helperText={`Reference fee: ${money(monthly)} a month. Enter what this client paid.`}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          />
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          loading={busy}
          disabled={n === null || g === null || !amountOk}
          onClick={submit}
        >
          {enabled ? 'Extend' : 'Enable'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function GraceDialog({
  tenant,
  current,
  onClose,
}: {
  tenant: Pick<TenantDetail, 'id' | 'name'>;
  current: number;
  onClose: () => void;
}) {
  const done = useDone(tenant.id, onClose);
  const [grace, setGrace] = useState(String(current));
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const g = intIn(grace, 0, 60);
  const submit = () => {
    if (g === null) return;
    setBusy(true);
    unwrap(
      api.POST('/platform/tenants/{id}/whatsapp/grace', {
        params: { path: { id: tenant.id } },
        body: { graceDays: g, ...(notes.trim() ? { notes: notes.trim() } : {}) },
      }),
    )
      .then(() => done(`WhatsApp grace set to ${String(g)} days`))
      .catch((e: unknown) => {
        setBusy(false);
        setError(e);
      });
  };
  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>WhatsApp grace</DialogTitle>
      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 1 }}>
          {error !== null && <Callout severity="error">{errorMessage(error)}</Callout>}
          <TextField
            label="Grace days"
            value={grace}
            onChange={(e) => setGrace(e.target.value)}
            error={g === null}
            helperText={`Replaces the current ${String(current)} days, counted from the end of the paid period (5 → 7 adds 2 days).`}
          />
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" loading={busy} disabled={g === null} onClick={submit}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DisableDialog({
  tenant,
  onClose,
}: {
  tenant: Pick<TenantDetail, 'id' | 'name'>;
  onClose: () => void;
}) {
  const done = useDone(tenant.id, onClose);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const submit = () => {
    setBusy(true);
    unwrap(
      api.POST('/platform/tenants/{id}/whatsapp/disable', {
        params: { path: { id: tenant.id } },
        body: notes.trim() ? { notes: notes.trim() } : {},
      }),
    )
      .then(() => done('WhatsApp turned off'))
      .catch((e: unknown) => {
        setBusy(false);
        setError(e);
      });
  };
  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Turn off WhatsApp?</DialogTitle>
      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 1 }}>
          {error !== null && <Callout severity="error">{errorMessage(error)}</Callout>}
          <Typography variant="body2">
            Everything WhatsApp is hidden from {tenant.name} again. Enabling it later starts a new
            period from that day.
          </Typography>
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" color="error" loading={busy} onClick={submit}>
          Turn off
        </Button>
      </DialogActions>
    </Dialog>
  );
}
